import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	inject,
	NgModule,
	OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationCancel, NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';

import { SessionService } from '~app-client/core/services';
import { HomeService } from '~app-client/home/services';
import { HomeNavigationKey } from '~app-client/home/types';
import { MenuPageSpecialKey } from '~app-client/node-creation-new/types';
import { NewNodeCreationService } from '~app-client-item/node-creation-new/services/node-creation.service';
import { ReachLogoLoaderModule } from '~app-ui/loader';
import { NavigationPhoneModule, NavigationSharedModule } from '~app-ui/navigation';
import { ReachUserThumbnailModule } from '~app-ui/thumbnails/users';

@Component({
	standalone: false,

	selector: 'home-navigation-menu',
	templateUrl: './navigation-menu.component.html',
	styleUrls: ['./navigation-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeNavigationMenuComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly sessionService = inject(SessionService);
	private readonly homePageService = inject(HomeService);
	private readonly router = inject(Router);

	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly cdRef = inject(ChangeDetectorRef);
	private readonly newNodeCreationService = inject(NewNodeCreationService);

	public keys = HomeNavigationKey;

	// Profile is not here because it has its own component
	public navigationIds: HomeNavigationKey[] = [
		HomeNavigationKey.CREATE_NODE,
		HomeNavigationKey.SEARCH,
		HomeNavigationKey.DASHBOARD,
	];
	public selectedPage?: HomeNavigationKey;

	public user$ = this.sessionService.user$;

	public loading = false;

	private navigationIconMap: Partial<Record<HomeNavigationKey, string>> = {
		[HomeNavigationKey.DASHBOARD]: 'icon-home',
		[HomeNavigationKey.CREATE_NODE]: 'icon-plus-add-square',
		[HomeNavigationKey.SEARCH]: 'icon-knowledge-base',
		[HomeNavigationKey.CHAT_AI]: 'icon-sparks-AI',
		[HomeNavigationKey.INTEGRATIONS]: 'icon-integrations',
	};

	ngOnInit(): void {
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				const firstChild = this.activatedRoute.firstChild?.snapshot;
				const firstChildData = firstChild?.data?.homeActiveRoute as
					| HomeNavigationKey
					| undefined;
				const secondChildData = firstChild?.firstChild?.data?.homeActiveRoute;
				this.selectedPage = firstChildData || secondChildData;
				this.cdRef.markForCheck();
			});
		this.activatedRoute.url.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			const firstChild = this.activatedRoute.firstChild?.snapshot;
			const firstChildData = firstChild?.data?.homeActiveRoute as
				| HomeNavigationKey
				| undefined;
			const secondChildData = firstChild?.firstChild?.data?.homeActiveRoute;
			this.selectedPage = firstChildData || secondChildData;
			this.cdRef.markForCheck();
		});
	}

	public async navigateTo(navigationId: string): Promise<void> {
		if (navigationId === this.keys.CREATE_NODE) {
			this.newNodeCreationService.openCreation(MenuPageSpecialKey.NODE_TYPE_SELECTOR);
			return;
		}

		if (this.selectedPage === navigationId) {
			this.homePageService.emitScrollTop();
		} else {
			this.router.events
				.pipe(
					filter(
						(event) =>
							event instanceof NavigationEnd || event instanceof NavigationCancel
					),
					take(1),
					takeUntilDestroyed(this.destroyRef)
				)
				.subscribe(() => {
					this.loading = false;
				});

			this.loading = true;

			this.router.navigate(['/', 'home', navigationId], {
				queryParamsHandling: 'preserve',
			});
		}
	}

	public navigationIcon(id: string): string {
		return this.navigationIconMap[id];
	}
}

@NgModule({
	declarations: [HomeNavigationMenuComponent],
	imports: [
		CommonModule,
		ReachUserThumbnailModule,
		NavigationPhoneModule,
		NavigationSharedModule,
		ReachLogoLoaderModule,
	],
	exports: [HomeNavigationMenuComponent],
})
export class HomeNavigationMenuModule {}
